// Generat automat din README.md
import React, { useState, useEffect } from 'react';
import { Shield, Activity, AlertTriangle, Eye, Users, Globe, Lock, Wifi, Server, Database, Clock, Filter, RefreshCw, Download, Bell, Settings } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const Monitorizare247 = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [systemStatus, setSystemStatus] = useState('operational');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Auto refresh data every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    const refreshTimer = setInterval(() => {
      // Simulate data refresh
      console.log('Refreshing monitoring data...');
    }, 30000);
    return () => clearInterval(refreshTimer);
  }, [autoRefresh]);

  // Mock data pentru monitorizarea securității
  const securityData = {
    systemStatus: {
      overall: 'operational', // operational, warning, critical
      uptime: '99.97%',
      lastIncident: '5 zile',
      activeMonitoring: true,
      protectedAssets: 47
    },
    realTimeStats: {
      activeConnections: 1247,
      blockedAttacks: 23,
      systemLoad: 67,
      networkTraffic: '2.4 GB/h',
      securityLevel: 'Înalt'
    },
    recentIncidents: [
      {
        id: 1,
        type: 'Tentativă Intruziune',
        severity: 'Înaltă',
        source: '185.234.125.67',
        target: 'Web Server #1',
        timestamp: '2024-03-15 14:32:15',
        status: 'Blocat',
        description: 'Multiple încercări de autentificare cu credențiale false pe portul SSH.',
        actions: ['IP blocat automat', 'Alertă trimisă echipei', 'Log securitate actualizat']
      },
      {
        id: 2,
        type: 'Trafic Suspiciolos',
        severity: 'Medie',
        source: '192.168.1.115',
        target: 'Database Server',
        timestamp: '2024-03-15 13:45:22',
        status: 'Monitorizat',
        description: 'Volum neobișnuit de cereri către baza de date în intervalul 13:40-13:45.',
        actions: ['Trafic monitorizat', 'Pattern analizat', 'Alertă preventivă']
      },
      {
        id: 3,
        type: 'Malware Detectat',
        severity: 'Înaltă',
        source: 'Email Gateway',
        target: 'Workstation #23',
        timestamp: '2024-03-15 12:18:45',
        status: 'Rezolvat',
        description: 'Attachment cu malware detectat și blocat în email-ul către user@company.ro.',
        actions: ['Fișier în carantină', 'Utilizator notificat', 'Sistem scanat']
      },
      {
        id: 4,
        type: 'Port Scan',
        severity: 'Scăzută',
        source: '94.142.76.89',
        target: 'Firewall',
        timestamp: '2024-03-15 11:55:12',
        status: 'Blocat',
        description: 'Scanare automată a porturilor externally facing services.',
        actions: ['IP adăugat în blacklist', 'Porturile verificate', 'Raport generat']
      }
    ],
    networkActivity: [
      {
        id: 1,
        service: 'Web Server',
        connections: 1247,
        status: 'Operational',
        responseTime: '45ms',
        cpu: 34,
        memory: 68,
        lastCheck: '30 sec'
      },
      {
        id: 2,
        service: 'Database Server',
        connections: 89,
        status: 'Operational', 
        responseTime: '12ms',
        cpu: 23,
        memory: 45,
        lastCheck: '30 sec'
      },
      {
        id: 3,
        service: 'Email Server',
        connections: 156,
        status: 'Warning',
        responseTime: '125ms',
        cpu: 78,
        memory: 82,
        lastCheck: '30 sec'
      },
      {
        id: 4,
        service: 'File Server',
        connections: 234,
        status: 'Operational',
        responseTime: '67ms',
        cpu: 45,
        memory: 56,
        lastCheck: '30 sec'
      }
    ],
    threatIntelligence: [
      {
        id: 1,
        threat: 'Botnet C2 Communication',
        riskLevel: 'Critic',
        affectedIPs: ['185.234.125.67', '94.142.76.89'],
        firstDetected: '2024-03-15 09:30:00',
        countryOrigin: 'Russia',
        blocked: true
      },
      {
        id: 2,
        threat: 'Phishing Campaign',
        riskLevel: 'Înalt',
        affectedIPs: ['203.45.67.89'],
        firstDetected: '2024-03-15 08:15:00',
        countryOrigin: 'China',
        blocked: true
      },
      {
        id: 3,
        threat: 'DDoS Preparation',
        riskLevel: 'Mediu',
        affectedIPs: ['167.89.123.45', '198.76.54.32'],
        firstDetected: '2024-03-15 07:45:00',
        countryOrigin: 'Unknown',
        blocked: false
      }
    ],
    complianceMetrics: {
      gdprCompliance: 98,
      iso27001: 95,
      pciDss: 100,
      lastAudit: '2024-02-15',
      nextAudit: '2024-08-15'
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Critic': 
      case 'Înaltă': return 'error';
      case 'Medie': return 'warning';
      case 'Scăzută': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Operational': return 'success';
      case 'Warning': return 'warning';
      case 'Critical': return 'error';
      case 'Blocat':
      case 'Rezolvat': return 'success';
      case 'Monitorizat': return 'warning';
      default: return 'default';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'Critic': return 'error';
      case 'Înalt': return 'warning';
      case 'Mediu': return 'info';
      case 'Scăzut': return 'success';
      default: return 'default';
    }
  };

  const incidentColumns = [
    { 
      header: 'Tip Incident', 
      key: 'type',
      render: (value) => (
        <div className="font-medium text-gray-900 dark:text-white">{value}</div>
      )
    },
    { 
      header: 'Severitate', 
      key: 'severity',
      render: (value) => <Badge variant={getSeverityColor(value)}>{value}</Badge>
    },
    { 
      header: 'Sursă', 
      key: 'source',
      render: (value) => <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{value}</code>
    },
    { 
      header: 'Țintă', 
      key: 'target'
    },
    { 
      header: 'Status', 
      key: 'status',
      render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge>
    },
    { 
      header: 'Timestamp', 
      key: 'timestamp',
      render: (value) => <span className="text-xs text-gray-500">{value}</span>
    },
    {
      header: 'Acțiuni',
      key: 'actions',
      render: (_, row) => (
        <button
          onClick={() => {
            setSelectedIncident(row);
            setShowIncidentModal(true);
          }}
          className="text-primary-600 hover:text-primary-800 text-sm"
        >
          Detalii
        </button>
      )
    }
  ];

  const networkColumns = [
    { 
      header: 'Serviciu', 
      key: 'service',
      render: (value) => (
        <div className="flex items-center space-x-2">
          <Server className="w-4 h-4 text-gray-500" />
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    { 
      header: 'Conexiuni Active', 
      key: 'connections',
      render: (value) => <span className="font-semibold text-primary-600">{value}</span>
    },
    { 
      header: 'Status', 
      key: 'status',
      render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge>
    },
    { 
      header: 'Răspuns', 
      key: 'responseTime'
    },
    { 
      header: 'CPU', 
      key: 'cpu',
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <div className="w-12 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${
                value > 80 ? 'bg-red-500' : value > 60 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${value}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium">{value}%</span>
        </div>
      )
    },
    { 
      header: 'Memorie', 
      key: 'memory',
      render: (value) => (
        <div className="flex items-center space-x-2">
          <div className="w-12 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${
                value > 80 ? 'bg-red-500' : value > 60 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${value}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium">{value}%</span>
        </div>
      )
    },
    { 
      header: 'Ultimul Check', 
      key: 'lastCheck',
      render: (value) => <span className="text-xs text-gray-500">{value}</span>
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Monitorizare Securitate 24/7</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Supraveghere continuă a sistemelor cu alertare instantanee pentru anomalii
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="w-4 h-4 mr-2" />
            Timpul curent: {currentTime.toLocaleString('ro-RO')}
            <span className="mx-2">•</span>
            <Activity className="w-4 h-4 mr-1" />
            <span className={`${systemStatus === 'operational' ? 'text-green-600' : 
              systemStatus === 'warning' ? 'text-yellow-600' : 'text-red-600'}`}>
              Sistemul operează normal
            </span>
          </div>
        </div>
        <div className="flex space-x-3">
          <button 
            className={`btn-secondary ${autoRefresh ? 'bg-primary-100 dark:bg-primary-800' : ''}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            Auto Refresh
          </button>
          <button className="btn-secondary">
            <Download className="w-4 h-4 mr-2" />
            Export Raport
          </button>
          <button 
            className="btn-primary"
            onClick={() => setShowConfigModal(true)}
          >
            <Settings className="w-4 h-4 mr-2" />
            Configurări
          </button>
        </div>
      </div>

      {/* System Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <Card title="Status General" subtitle="Sistemul principal" icon={Shield}>
          <div className="flex items-center space-x-3">
            <div className={`w-4 h-4 rounded-full ${
              securityData.systemStatus.overall === 'operational' ? 'bg-green-500' :
              securityData.systemStatus.overall === 'warning' ? 'bg-yellow-500' : 'bg-red-500'
            } animate-pulse`}></div>
            <div>
              <div className="text-lg font-bold text-green-600">Operațional</div>
              <div className="text-sm text-gray-500">Uptime: {securityData.systemStatus.uptime}</div>
            </div>
          </div>
        </Card>
        
        <Card title="Conexiuni Active" subtitle="În timp real" icon={Users}>
          <div className="text-2xl font-bold text-primary-600">{securityData.realTimeStats.activeConnections}</div>
          <div className="text-sm text-blue-600">+15 în ultimele 5 min</div>
        </Card>
        
        <Card title="Atacuri Blocate" subtitle="Astăzi" icon={Shield}>
          <div className="text-2xl font-bold text-primary-600">{securityData.realTimeStats.blockedAttacks}</div>
          <div className="text-sm text-red-600">5 în ultima oră</div>
        </Card>
        
        <Card title="Încărcare Sistem" subtitle="Resurse utilizate" icon={Activity}>
          <div className="flex items-center space-x-3">
            <div className="flex-1">
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                <div 
                  className={`h-2 rounded-full ${
                    securityData.realTimeStats.systemLoad > 80 ? 'bg-red-500' :
                    securityData.realTimeStats.systemLoad > 60 ? 'bg-yellow-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${securityData.realTimeStats.systemLoad}%` }}
                ></div>
              </div>
              <div className="text-lg font-bold text-primary-600">{securityData.realTimeStats.systemLoad}%</div>
            </div>
          </div>
        </Card>
        
        <Card title="Nivel Securitate" subtitle="AI Assessment" icon={Lock}>
          <div className="text-2xl font-bold text-green-600">{securityData.realTimeStats.securityLevel}</div>
          <div className="text-sm text-green-600">Toate sistemele securizate</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Security Incidents */}
        <div className="lg:col-span-2">
          <Card title="Incidente de Securitate Recente" subtitle="Ultimele 24 de ore">
            <div className="mb-4 flex items-center space-x-4">
              <select className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-sm">
                <option>Toate severitățile</option>
                <option>Critic</option>
                <option>Înaltă</option>
                <option>Medie</option>
                <option>Scăzută</option>
              </select>
              <select className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-sm">
                <option>Toate statusurile</option>
                <option>Blocat</option>
                <option>Monitorizat</option>
                <option>Rezolvat</option>
              </select>
              <button className="btn-secondary btn-sm">
                <Filter className="w-3 h-3 mr-1" />
                Filtrează
              </button>
            </div>
            <Table columns={incidentColumns} data={securityData.recentIncidents} />
          </Card>
        </div>

        {/* Live Threat Intelligence */}
        <div className="space-y-6">
          <Card title="Threat Intelligence" icon={AlertTriangle}>
            <div className="space-y-4">
              {securityData.threatIntelligence.map((threat) => (
                <div key={threat.id} className="p-3 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded-r-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant={getRiskColor(threat.riskLevel)} className="text-xs">
                      {threat.riskLevel}
                    </Badge>
                    <Badge variant={threat.blocked ? 'success' : 'warning'} className="text-xs">
                      {threat.blocked ? 'Blocat' : 'Monitorizat'}
                    </Badge>
                  </div>
                  <div className="font-medium text-gray-900 dark:text-white text-sm mb-1">
                    {threat.threat}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                    <div>IPs afectate: {threat.affectedIPs.length}</div>
                    <div>Origine: {threat.countryOrigin}</div>
                    <div>Detectat: {new Date(threat.firstDetected).toLocaleString('ro-RO')}</div>
                  </div>
                </div>
              ))}
            </div>
            <button className="w-full mt-4 btn-primary btn-sm">
              Vezi Threat Intelligence Complet
            </button>
          </Card>

          <Card title="Compliance Status" icon={Shield}>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">GDPR</span>
                <div className="flex items-center space-x-2">
                  <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{ width: `${securityData.complianceMetrics.gdprCompliance}%` }}></div>
                  </div>
                  <span className="text-sm font-medium">{securityData.complianceMetrics.gdprCompliance}%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">ISO 27001</span>
                <div className="flex items-center space-x-2">
                  <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{ width: `${securityData.complianceMetrics.iso27001}%` }}></div>
                  </div>
                  <span className="text-sm font-medium">{securityData.complianceMetrics.iso27001}%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">PCI DSS</span>
                <div className="flex items-center space-x-2">
                  <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{ width: `${securityData.complianceMetrics.pciDss}%` }}></div>
                  </div>
                  <span className="text-sm font-medium">{securityData.complianceMetrics.pciDss}%</span>
                </div>
              </div>
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 space-y-1">
                  <div>Ultimul audit: {securityData.complianceMetrics.lastAudit}</div>
                  <div>Următorul audit: {securityData.complianceMetrics.nextAudit}</div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Network Services Status */}
      <Card title="Status Servicii de Rețea" subtitle="Monitorizare în timp real a serviciilor critice">
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Ultima actualizare: {currentTime.toLocaleTimeString('ro-RO')}
          </div>
          <button className="btn-secondary btn-sm">
            <RefreshCw className="w-3 h-3 mr-1" />
            Refresh Manual
          </button>
        </div>
        <Table columns={networkColumns} data={securityData.networkActivity} />
      </Card>

      {/* Incident Details Modal */}
      <Modal
        isOpen={showIncidentModal}
        onClose={() => {
          setShowIncidentModal(false);
          setSelectedIncident(null);
        }}
        title={`Detalii Incident: ${selectedIncident?.type}`}
        size="large"
      >
        {selectedIncident && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Informații Generale</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Tip:</span>
                    <span className="text-sm">{selectedIncident.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Severitate:</span>
                    <Badge variant={getSeverityColor(selectedIncident.severity)}>{selectedIncident.severity}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
                    <Badge variant={getStatusColor(selectedIncident.status)}>{selectedIncident.status}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Timestamp:</span>
                    <span className="text-sm">{selectedIncident.timestamp}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Sursă:</span>
                    <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{selectedIncident.source}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Țintă:</span>
                    <span className="text-sm">{selectedIncident.target}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Acțiuni Automate</h3>
                <div className="space-y-2">
                  {selectedIncident.actions.map((action, index) => (
                    <div key={index} className="flex items-center space-x-2 text-sm">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>{action}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Descriere Detaliată</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                {selectedIncident.description}
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button className="btn-secondary">Export PDF</button>
              <button className="btn-secondary">Adaugă la Raport</button>
              <button 
                className="btn-primary"
                onClick={() => {
                  setShowIncidentModal(false);
                  setSelectedIncident(null);
                }}
              >
                Închide
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Configuration Modal */}
      <Modal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        title="Configurări Monitorizare"
      >
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Setări Alertare</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Email Alerts</span>
                <input type="checkbox" className="rounded" defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">SMS Alerts</span>
                <input type="checkbox" className="rounded" defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Slack Notifications</span>
                <input type="checkbox" className="rounded" />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Auto Refresh (30s)</span>
                <input 
                  type="checkbox" 
                  className="rounded" 
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Threshold-uri Alertare</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  CPU Usage (%)
                </label>
                <input
                  type="number"
                  defaultValue="80"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Memory Usage (%)
                </label>
                <input
                  type="number"
                  defaultValue="85"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Response Time (ms)
                </label>
                <input
                  type="number"
                  defaultValue="100"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Failed Login Attempts
                </label>
                <input
                  type="number"
                  defaultValue="5"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-sm"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button 
              className="btn-secondary"
              onClick={() => setShowConfigModal(false)}
            >
              Anulează
            </button>
            <button className="btn-primary">
              Salvează Configurările
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Monitorizare247;